/**
 * Column geometry for the inbox table. The grid runs `table-layout: fixed`, so these widths are
 * authoritative: each column the desk controls the vocabulary of is budgeted for its widest value —
 * pill, menu chevron and the cell's own 10px padding included — so values render whole instead of
 * falling back to an ellipsis.
 */

/**
 * Subject, contact and account carry the row's free text, whose length nobody controls. Contact and
 * account hold this width and truncate past it, with the full value on the cell's tooltip. Subject
 * is the column a desk scans, so its column renders at `auto` and takes every pixel the budgeted
 * columns leave over — for subject this is only the floor it holds at the table's minimum width.
 */
const FREE_TEXT_COLUMN_WIDTH = 208;

/**
 * Marker slot, then the bare number. The slot is held open on every row so the numbers share an x
 * (see `ticketTableStyles`), and the `#` heading is indented by the same slot so it sits over them.
 * Seven digits at Lexend 14px: truncating a number would show a different ticket, so the column
 * holds the whole thing.
 */
export const TICKET_NUMBER_MARKER_SLOT_WIDTH = 14;
export const TICKET_NUMBER_MARKER_GAP = 6;
const SEVEN_DIGIT_NUMBER_WIDTH = 56;
/** DataGrid's own horizontal cell padding, which these budgets have to cover. */
export const CELL_SIDE_PADDING = 10;

export const TICKET_TABLE_COLUMN_WIDTHS = {
	select: 44,
	number: TICKET_NUMBER_MARKER_SLOT_WIDTH + TICKET_NUMBER_MARKER_GAP + SEVEN_DIGIT_NUMBER_WIDTH + 2 * CELL_SIDE_PADDING,
	subject: FREE_TEXT_COLUMN_WIDTH,
	contact: FREE_TEXT_COLUMN_WIDTH,
	account: FREE_TEXT_COLUMN_WIDTH,
	// Holds the status pill, its chevron, and the compact channel badge beside them.
	status: 152,
	priority: 124,
	// Fits the longest category name Levitate's desks ship with; longer ones a desk defines for
	// itself truncate inside the pill, with the full name on the cell's tooltip.
	category: 216,
	assignee: 164,
	// Sized by the header label, which is wider than the timestamps below it.
	lastUpdate: 128,
} as const;

export type TicketTableColumnKey = keyof typeof TICKET_TABLE_COLUMN_WIDTHS;

const COLUMNS_WITH_ACCOUNT = [
	'select',
	'number',
	'subject',
	'contact',
	'account',
	'status',
	'priority',
	'category',
	'assignee',
	'lastUpdate',
] as const satisfies readonly TicketTableColumnKey[];

const COLUMNS_WITHOUT_ACCOUNT = [
	'select',
	'number',
	'subject',
	'contact',
	'status',
	'priority',
	'category',
	'assignee',
	'lastUpdate',
] as const satisfies readonly TicketTableColumnKey[];

const sumColumnWidths = (columns: readonly TicketTableColumnKey[]): number =>
	columns.reduce((total, column) => total + TICKET_TABLE_COLUMN_WIDTHS[column], 0);

export const TICKET_TABLE_MIN_WIDTH_WITH_ACCOUNT = sumColumnWidths(COLUMNS_WITH_ACCOUNT);

export const TICKET_TABLE_MIN_WIDTH_WITHOUT_ACCOUNT = sumColumnWidths(COLUMNS_WITHOUT_ACCOUNT);

export function getTicketTableColumns(showAccountColumn: boolean): readonly TicketTableColumnKey[] {
	return showAccountColumn ? COLUMNS_WITH_ACCOUNT : COLUMNS_WITHOUT_ACCOUNT;
}
